import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';

import {MCP_API_URL} from '../../../settings/local_settings';
import {CheckCircle, Copy, DownloadSimple, Question, Robot} from '../../icons';
import {fetchAgencyClients, fetchConnections, fetchDataReadiness, fetchOnboardingStatus,
        forgetOnboardingStatusCache} from '../api';
import {connectedPlatformsCountOf} from './platformsResult';
import {messageKeyOf, readinessFrom, retryDelayFor, syncStateOf,
        warnsAboutMissingData} from '../syncProgress';
import {clearDecisions} from '../wizardStorage';
import {useOnboarding} from '../wizardContext';
import {clientGroupsOf} from '../clientGroups';
import {copyPlainText} from '../../../utils/copyPlainText';
import {useLearning} from '../../learn/useLearning';
import {CLARITY_UNMASK} from '../../../utils/clarity/unmask';

/**
 * El aha-moment: la IA respondiendo sobre la cartera que la agencia acaba de cargar.
 *
 * Dos decisiones sostienen la pantalla:
 *
 * - **La pregunta de ejemplo nombra a un cliente REAL de la agencia**, el de mayor inversión. Es la
 *   diferencia entre una demo y el momento en que el producto se vuelve suyo.
 * - **La primera consulta se detecta sola, pero sólo se CELEBRA la que pasó mirando.** Cuando el
 *   asistente le pregunta algo a Dashbo, la pantalla lo ve y pasa sola. Lo que no puede hacer es
 *   afirmarlo sin haberlo visto: ver `firstQueryStateFrom`.
 * - **La espera de los datos se narra, no se esconde.** Una agencia nueva nace sin datos
 *   sincronizados, así que mientras conecta su asistente el sync todavía está corriendo. Se espera
 *   —eso está decidido— pero diciendo qué pasa y cuánto falta: una espera visible y muda es peor
 *   que no esperar. Se avisa y NO se bloquea: la pregunta de ejemplo sigue disponible siempre.
 *
 * **Son dos polls y no uno**, con contadores de fallo separados. Miran cosas distintas (el estado
 * de la puesta en marcha, cacheado 30 s; y la disponibilidad de los datos, fresca y a la cadencia
 * que pide el servidor), y sobre todo: que se caiga uno no puede apagar al otro. Compartir timer
 * obligaría al más lento a gobernar al más rápido.
 */

const POLL_MS = 10000;
const MAX_POLL_MS = 10 * 60 * 1000;
const MAX_FAILURES = 3;
// Cuánto esperar si el servidor no dice cada cuánto volver a preguntar.
const DEFAULT_READINESS_RETRY_S = 10;
// Cuánto dura el «copiado» antes de volver al rótulo normal.
const COPIED_MS = 2000;
// Cuánto queda a la vista el aviso de qué hacer con la pregunta copiada (H-06): es una frase para
// leer, no un rótulo de botón.
export const PASTE_HINT_MS = 4000;

/**
 * Los asistentes que se ofrecen, y qué hace falta de verdad para conectar cada uno.
 *
 * La matriz se verificó contra la documentación de cada proveedor el 2026-09-04, porque el mockup
 * afirmaba un mínimo de USD 20/mes para todos y eso es falso (card LBxVTOS9):
 *
 * - **Claude** — un conector propio anda en TODOS los planes, incluido el gratuito, que admite
 *   exactamente uno. Dashbo entra en ese uno: es el camino más barato y por eso el recomendado.
 * - **Claude Code** — no tiene pantalla de conectores: se agrega con un comando que lleva el token
 *   de la agencia. Ese token vive en la guía completa, así que este paso enlaza en vez de copiar una
 *   receta que quedaría desincronizada.
 * - **ChatGPT** — un servidor MCP propio se conecta también con el plan GRATUITO. Hasta el 29/09 esto
 *   decía «requiere Plus»: fede lo verificó en vivo con una cuenta gratis (Dashbo quedó en
 *   «Instalados») después de haber pagado Plus por ese cartel (card J2tCKtaJ). La barrera inventada
 *   costó una suscripción. Desde el 05/10/2026 ChatGPT ya no pide el Modo desarrollador: se agrega
 *   desde Complementos → Agregar → «Agregar servidor MCP personalizado», y la cuenta gratis de fede
 *   lo siguió conectando (12/10).
 * - **Otro asistente** — cualquier cliente MCP (Cursor, LibreChat, el que venga) se conecta pegando
 *   esta misma dirección, así que la opción no promete nada que no podamos cumplir.
 *
 * ❌ **Gemini se sacó el 23/09 (card CYzf5yVV).** Estaba ofrecido y no se podía conectar: soporta MCP
 * por dos puertas que no son la que la agencia tiene —Gemini Enterprise, que habilita un
 * administrador, o la app de consumo, que pide cuenta personal, plan AI Ultra y estar en EE.UU.— y
 * con el Gemini que viene en Workspace no alcanza. Ofrecer una opción que termina en «no se puede»
 * cuesta más que no ofrecerla: Pablo, al verlo en la reunión del 17/09, «Gemini, esto es mentira,
 * ¿no?». **No lo repongas sin una puerta que la agencia promedio pueda abrir sola.**
 *
 * ⚠️ **Si esto cambia, se corrige acá y en `dashbo_mcp.setup.*`** — son las dos superficies donde
 * Dashbo afirma qué hace falta para conectar. (Esa otra todavía nombra a Gemini: card aparte.)
 */
const ASSISTANTS = ['claude', 'claude_code', 'chatgpt', 'other', 'none'];

/**
 * El que no usa ninguna de estas herramientas.
 *
 * «Hay gente todavía en el mundo que no usa ChatGPT o Claude… le tenemos que dar una opción»
 * (Pablo). Sin esta salida, el paso no tiene respuesta para quien no tiene asistente ni cuenta paga:
 * o abandona el recorrido por el costado, o finge que conectó algo.
 */
const NO_ASSISTANT = 'none';

/**
 * Qué puede AFIRMAR la pantalla sobre la primera consulta, a partir de lo que contesta el servidor.
 *
 * `first_assistant_query.done` es un hecho **histórico y de la AGENCIA**: lo deriva
 * `AgencyOnboardingService._is_done` de todas las interacciones del asistente, sin ventana de tiempo
 * y sin mirar quién las hizo («¿alguna vez lo conectaste?», dice su docstring). Encontrarlo prendido
 * **al entrar** sólo prueba que alguien consultó alguna vez — puede haber sido en enero, o un
 * compañero. Tratar eso como «acabás de hacer tu primera consulta» fue el bug que reportó Pablo el
 * 17/09: el paso saltaba solo a la pantalla de celebración sin que él hubiera preguntado nada.
 *
 * Por eso la primera respuesta fija una LÍNEA DE BASE y nunca celebra. Los tres estados:
 *
 * - `pending` — al entrar no había ninguna consulta. Lo que llegue ahora sí es del usuario.
 * - `prior`   — ya había consultas antes de entrar. Es cierto y se dice, pero no es «la primera».
 * - `fresh`   — la vimos pasar de no a sí mientras el usuario miraba. La única que se celebra.
 *
 * Pura y sin estado a propósito: es la regla que separa lo verdadero de lo verosímil, y se testea
 * sola.
 */
export function firstQueryStateFrom(done, baseline) {
    if (baseline === null || baseline === undefined) return done ? 'prior' : 'pending';
    if (baseline === true) return 'prior';
    return done ? 'fresh' : 'pending';
}

/**
 * Qué dice el renglón de abajo, según lo que la pantalla sabe en ese momento.
 *
 * Las cuatro frases son distintas y ninguna puede usarse por otra:
 *
 * - **Todavía no sabemos** (`null`) — el estado tarda lo suyo (medido en el QA del 23/09: **7,5 s**
 *   contra el backend local), y durante esa ventana la pantalla le pedía «hacé tu primera consulta»
 *   a alguien que ya la había hecho. Decir que estamos mirando es lo único cierto ahí.
 * - **Ya consultó antes** (`prior`) — cierto, pero no es «la primera».
 * - **Falta hacerla, con la pregunta a la vista** — manda a copiarla.
 * - **Falta hacerla y NO hay pregunta** — el bloque de ejemplo necesita un cliente; sin él,
 *   «copiá la pregunta de arriba» señala a la nada.
 */
export function waitingKeyOf(firstQuery, question) {
    if (firstQuery === null || firstQuery === undefined) return 'onboarding.ia.checking_state';
    if (firstQuery === 'prior') return 'onboarding.ia.already_queried';
    return question ? 'onboarding.ia.waiting' : 'onboarding.ia.waiting_no_question';
}

/** A dónde sale el túnel desde este paso (D8): el listado de clientes 2.1, nunca el Tablero clásico. */
const TUNNEL_EXIT = '/agency/clients';

/** Los que no se conectan pegando la URL en una pantalla de conectores. */
const SENDS_TO_GUIDE = ['claude_code', NO_ASSISTANT];

/** Los que llevan una advertencia de plan o de disponibilidad debajo de los pasos. */
const HAS_REQUIREMENT = ['claude', 'claude_code', 'chatgpt'];

/**
 * Los que aclaran EN LA ETIQUETA de qué producto se trata.
 *
 * Claude y Claude Code aparecían uno al lado del otro sin explicación y ni fede ni Pablo pudieron
 * distinguirlos a primera vista. Son productos distintos: claude.ai y la aplicación de escritorio se
 * conectan por la misma pantalla de Conectores —por eso comparten chip, y el paso 1 ya lo dice—,
 * mientras que Claude Code vive en la terminal y se agrega con un comando.
 */
const HAS_HINT = ['claude', 'claude_code'];

/** Los que tienen un formulario del otro lado (nombre, autenticación) que conviene anticipar. */
const HAS_NAME_HINT = ['claude', 'chatgpt'];

/**
 * Los que dejan subir un ícono para el conector. El isotipo lo sirve el propio Frontend y su URL de
 * producción la usan también los artículos del Centro de Ayuda: si se mueve, cambiarlo allá.
 */
const HAS_ICON_FIELD = ['chatgpt'];
const DASHBO_ICON_URL = `${process.env.PUBLIC_URL || ''}/dashbo-icono.png`;

/**
 * Lo que el backend cuenta de la primera consulta (`first_assistant_query.detail`, heWJCM8k B).
 *
 * Puede no venir —el Frontend y el Backend no se deployan juntos—, y entonces la celebración sale
 * sin recibo. Nunca se inventa: sin el dato, no hay nada que decir sobre QUÉ se consultó.
 */
function firstQueryDetailOf(response) {
    const steps = response?.data?.steps || [];
    return steps.find(step => step.key === 'first_assistant_query')?.detail || null;
}

export function StepConnectAI() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {state, server} = useOnboarding();
    // Las dos frases que mandan a seguir «desde Tus primeros pasos» nombran «Aprende Dashbo» cuando
    // la persona tiene la página (copy.md §5 de la tanda 2026-10-03-aprende-dashbo): con el flag
    // prendido el drawer viejo deja de existir.
    const learnSuffix = useLearning().model?.enabled === true ? '_learn' : '';
    const [assistant, setAssistant] = useState('claude');
    const [firstQuery, setFirstQuery] = useState(null);
    const [firstQueryDetail, setFirstQueryDetail] = useState(null);
    const [checking, setChecking] = useState(false);
    const [asking, setAsking] = useState(false);
    const [statusAvailable, setStatusAvailable] = useState(true);
    const [copied, setCopied] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(false);
    // La vuelta agendada de cada aviso, por aviso: la de la pregunta y la de la dirección.
    const copiedTimers = useRef({});
    const [syncState, setSyncState] = useState(null);
    const startedAt = useRef(Date.now());
    const timer = useRef(null);
    const syncTimer = useRef(null);
    // Lo que el servidor decía la PRIMERA vez que contestó. `null` = todavía no lo sabemos. Vive en
    // un ref y no en el efecto porque el botón de verificar también la necesita: si estuviera
    // adentro del poll, apretarlo antes del primer tick no tendría contra qué comparar.
    const baseline = useRef(null);

    // Sólo la consulta detectada EN VIVO cierra el paso y muestra la celebración.
    const detected = firstQuery === 'fresh';
    // El valor de AHORA, para los callbacks que resuelven tarde y leerían el del render que los
    // creó (ver la guarda de orden de `confirmFirstQuery`).
    const firstQueryRef = useRef(firstQuery);
    firstQueryRef.current = firstQuery;

    const [fallbackClient, setFallbackClient] = useState(null);

    // El cliente del recorrido, si esta pestaña lo tiene en memoria.
    const clientFromTheTunnel = useMemo(() => biggestClientNameOf(state, server), [state, server]);

    // …y si no lo tiene, se pregunta. `server.candidates` vive SÓLO en memoria de la pestaña, y esta
    // pantalla se alcanza también desde el drawer y recargando: por ahí llegaba sin cliente y la
    // pregunta salía «¿Cómo viene el gasto de este mes?», con un doble espacio donde iba el nombre.
    // Justo la frase que es la promesa del recorrido.
    useEffect(() => {
        if (clientFromTheTunnel) return undefined;

        let alive = true;
        // El `.catch` es el mismo que los dos polls de abajo, y por el mismo motivo: un reject
        // —la red caída— dejaría una unhandled rejection. Acá no hay nada que reintentar; sin
        // nombre de cliente la pantalla ya tiene su camino (`waiting_no_question`), así que
        // alcanza con no romper.
        fetchAgencyClients().then(response => {
            if (!alive || response?.status !== 'OK') return;
            const list = Array.isArray(response.data) ? response.data
                                                      : (response.data?.agency_clients || response.data?.clients || []);
            const first = list.find(client => client && client.name);
            if (first) setFallbackClient(first.name);
        }).catch(() => {});
        return () => { alive = false; };
    }, [clientFromTheTunnel]);

    const exampleClient = clientFromTheTunnel || fallbackClient;
    const question = exampleClient
        ? t('onboarding.ia.example_question', {client: exampleClient, interpolation: {escapeValue: false}})
        : null;

    useEffect(() => {
        if (detected) return undefined;

        // `alive` corta la cadena al desmontar: sin esto, un pedido en vuelo cuando el usuario
        // navega agenda el siguiente ciclo DESPUÉS del cleanup, y la pantalla muerta sigue
        // preguntando cada diez segundos durante los diez minutos del tope.
        let alive = true;
        let failures = 0;

        const check = (response) => {
                if (!alive) return;
                if (response?.status !== 'OK') {
                    // Un corte transitorio no puede apagar la detección para siempre: se reintenta
                    // unas veces y recién ahí se deja de prometer que va a pasar sola.
                    failures += 1;
                    if (failures >= MAX_FAILURES) {
                        setStatusAvailable(false);
                        return;
                    }
                } else {
                    failures = 0;
                    const steps = response.data?.steps || [];
                    const query = steps.find(step => step.key === 'first_assistant_query');
                    const done = Boolean(query?.done);
                    const next = firstQueryStateFrom(done, baseline.current);
                    if (baseline.current === null) baseline.current = done;
                    if (next === 'fresh') setFirstQueryDetail(firstQueryDetailOf(response));
                    setFirstQuery(next);
                    // Se sigue preguntando MIENTRAS la respuesta pueda cambiar, que es sólo en
                    // `pending`. `fresh` es terminal por definición; y `prior` también, aunque no
                    // lo parezca: con la línea de base en `true`, `firstQueryStateFrom` devuelve
                    // `prior` para siempre, así que seguir preguntando son ~60 GET sin caché —con
                    // sus nueve EXISTS cada uno— que no pueden mover la pantalla. Y es el camino
                    // MÁS común: toda agencia que ya consultó alguna vez entra por acá.
                    // La tarjeta del menú lee el estado CACHEADO: sin esto puede mostrar la
                    // primera consulta como pendiente hasta 30 s después de celebrarla.
                    if (next === 'fresh') forgetOnboardingStatusCache();
                    if (next !== 'pending') return;
                }
                if (Date.now() - startedAt.current < MAX_POLL_MS) {
                    timer.current = setTimeout(tick, POLL_MS);
                }
        };

        const tick = () => {
            if (!alive) return;
            // Mismo motivo que en el poll de abajo: un reject tiene que contar como fallo y
            // reprogramar, no matar la deteccion en silencio.
            //
            // `cache: false` no es una optimización: el estado se sirve cacheado 30 s porque la
            // tarjeta del menú lo pide en cada navegación, y una línea de base leída de esa copia
            // puede ser de medio minuto antes de entrar — justo el dato que tiene que ser fresco.
            fetchOnboardingStatus({cache: false}).then(check).catch(() => check(null));
        };

        // Inmediato y no a los diez segundos: la línea de base se toma al ENTRAR. Hasta que exista,
        // la pantalla no puede distinguir «ya venía consultado» de «acaba de pasar».
        tick();
        return () => {
            alive = false;
            clearTimeout(timer.current);
        };
    }, [detected]);

    // El estado de los datos. Se apaga solo en cuanto deja de poder cambiar —esa es la diferencia
    // entre «todavía puede llegar» y «falta»— y también cuando ya hubo respuesta: si la agencia
    // preguntó y le contestaron, la espera es discutible.
    useEffect(() => {
        if (detected) return undefined;

        let alive = true;
        let failures = 0;

        // Un ciclo del poll. `response` llega en null cuando el pedido ni siquiera respondio: se
        // trata igual que una respuesta mala, porque para la pantalla significan lo mismo —todavia
        // no hay dato— y las dos tienen que sumar al contador de reintentos.
        const advance = (response) => {
            if (!alive) return;
            const readiness = readinessFrom(response);
            // `serverIsStillWorking` es el backend diciendo «te contesto, pero todavía no sé»: no es
            // un fallo y no tiene que abrir la cadencia. Contarlo como fallo era romper el contrato
            // que el propio endpoint declara al responder con la fase vacía y `pending: true`.
            if (readiness.available || readiness.serverIsStillWorking) failures = 0;
            else failures += 1;

            const next = syncStateOf(readiness, {
                elapsedMs: Date.now() - startedAt.current, maxWaitMs: MAX_POLL_MS,
            });
            setSyncState(next);
            if (!next.keepPolling) return;
            const seconds = retryDelayFor(next, failures, {base: DEFAULT_READINESS_RETRY_S});
            syncTimer.current = setTimeout(ask, seconds * 1000);
        };

        const ask = () => {
            if (!alive) return;
            // El `.catch` no es defensa de mas: sin el, una promesa rechazada —la red caida, que es
            // justo lo que este bloque existe para sobrevivir— no sumaba al contador, no
            // reprogramaba nada y dejaba una unhandled rejection.
            fetchDataReadiness().then(advance).catch(() => advance(null));
        };

        ask();
        return () => {
            alive = false;
            clearTimeout(syncTimer.current);
        };
    }, [detected]);

    const syncMessageKey = messageKeyOf(syncState);

    // Quien no tiene asistente no tiene dónde pegar la pregunta ni consulta que esperar: la pantalla
    // se queda con la explicación y la salida. Mostrarle el resto sería darle tarea sin herramienta.
    const noAssistant = assistant === NO_ASSISTANT;

    const connectorUrl = `${MCP_API_URL || 'https://mcp.dashbo.io'}/mcp`;

    /**
     * Cómo se nombra al asistente DENTRO de una frase, y dónde se le pregunta.
     *
     * La etiqueta del chip sirve para elegir en una fila; metida en medio de una oración, no: «*
     * Pregúntasela a **Otro asistente***» pone una mayúscula en el medio y se lee como el nombre de
     * un producto que no existe. Y «en un chat nuevo» es falso para Claude Code, que no tiene chats
     * sino sesiones en la terminal — el mismo paso que un renglón más arriba dice eso. Las dos cosas
     * las vio el QA del 23/09 (QA-tunel-ia-14), en español y en inglés.
     */
    const assistantInSentence = assistant === 'other'
        ? t('onboarding.ia.assistants.other_inline')
        : t(`onboarding.ia.assistants.${assistant}`);
    const whereToAsk = assistant === 'claude_code'
        ? t('onboarding.ia.in_a_new_session')
        : t('onboarding.ia.in_a_new_chat');
    const sentenceParts = {assistant: assistantInSentence, where: whereToAsk,
                           interpolation: {escapeValue: false}};

    // Al listado de clientes y no a `/`, que es el Tablero clásico: Leo tocó «Lo hago después» y cayó
    // en una pantalla vieja y vacía justo cuando empezaba a mirar (D8, card HMy64fKi). Ahí es donde
    // «Tus primeros pasos» lo sigue guiando. Es la salida de TODOS los botones de este paso.
    const leaveTunnel = () => {
        clearDecisions();
        navigate(TUNNEL_EXIT);
    };

    /**
     * Copiar y avisarlo, pero sólo por un rato.
     *
     * «Dirección copiada» se quedaba para siempre (QA del 23/09): un botón que cambió de texto hace
     * cinco minutos ya no informa nada, y encima crece de 81 a 148 px y mueve la fila. Vuelve solo.
     * Los timers se cancelan al desmontar porque si no, la vuelta cae sobre un componente que ya no
     * está.
     *
     * Copiar otra vez cancela la vuelta de la copia anterior del MISMO aviso: si no, la primera lo
     * borraba a mitad de la segunda (copiar a los 3,9 s lo dejaba 100 ms; review C-rp-fe-alta-03).
     */
    const copyAndFlag = (key, text, setFlag, ms = COPIED_MS) => {
        copyPlainText(text).then(() => {
            clearTimeout(copiedTimers.current[key]);
            setFlag(true);
            copiedTimers.current[key] = setTimeout(() => setFlag(false), ms);
        }, () => setFlag(false));
    };

    useEffect(() => () => Object.values(copiedTimers.current).forEach(clearTimeout), []);

    /**
     * «Ya hice mi primera consulta»: se VERIFICA contra el servidor en vez de creerle al botón.
     *
     * Lo que este botón hacía antes era `setAnswered(true)` a secas, o sea afirmar que hubo una
     * consulta porque alguien apretó un botón que dice que la hubo. De ahí salía la pantalla
     * «Primera consulta, respondida» sobre cero consultas.
     *
     * Sin respuesta del servidor tampoco se da por buena: se pregunta. Asumir que sí ante un corte
     * de red es la misma mentira con otra excusa.
     */
    const confirmFirstQuery = () => {
        setChecking(true);
        let detail = null;
        fetchOnboardingStatus({cache: false})
            .then(response => {
                if (response?.status !== 'OK') return null;
                const steps = response.data?.steps || [];
                detail = firstQueryDetailOf(response);
                return Boolean(steps.find(step => step.key === 'first_assistant_query')?.done);
            })
            .catch(() => null)
            .then(done => {
                setChecking(false);
                // Si el poll detectó la consulta mientras este pedido viajaba, ya está: una
                // respuesta más vieja no puede desarmar la celebración. Sin esta guarda, un `done`
                // en `false` que llega tarde desmonta la pantalla de «Primera consulta,
                // respondida» y la vuelve a montar — el usuario ve un parpadeo.
                if (firstQueryRef.current === 'fresh') return;
                if (done === null) {
                    setAsking(true);
                    return;
                }
                const next = firstQueryStateFrom(done, baseline.current);
                if (baseline.current === null) baseline.current = done;
                if (next === 'fresh') setFirstQueryDetail(detail);
                setFirstQuery(next);
                // `prior` no abre la pregunta: el pie ya le ofrece cerrar el paso, y volver a
                // preguntarle por una consulta que sí existe sería discutirle un hecho.
                if (next === 'pending') setAsking(true);
            });
    };

    if (detected) {
        return <FirstQueryAnswered detail={firstQueryDetail} state={state} onFinish={leaveTunnel}/>;
    }

    return (
        <div className="ob-step-body">
            <header className="ob-head -center">
                <h1 {...CLARITY_UNMASK}>{t('onboarding.ia.title')}</h1>
                <p {...CLARITY_UNMASK}>{t('onboarding.ia.subtitle')}</p>
            </header>

            <div className="ob-assistants" role="radiogroup" aria-label={t('onboarding.ia.pick_assistant')}>
                {ASSISTANTS.map(each => (
                    <button type="button" key={each} role="radio" aria-checked={assistant === each}
                            className={`ob-assistant${assistant === each ? ' -on' : ''}`}
                            onClick={() => setAssistant(each)}>
                        <span className="ob-assistant__name" {...CLARITY_UNMASK}>{t(`onboarding.ia.assistants.${each}`)}</span>
                        {HAS_HINT.includes(each) && (
                            <span className="ob-assistant__hint">
                                {t(`onboarding.ia.assistants.${each}_hint`)}
                            </span>
                        )}
                        {each === 'claude' && (
                            <span className="ob-tag -ia">{t('onboarding.ia.recommended')}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Los que no se conectan pegando una URL no fingen que sí: mandan a la guía, que tiene
                la receta con el token de la agencia. Repetirla acá era garantizar que las dos
                versiones se separaran. Con «no tengo ninguna» el destino es el mismo por otra razón:
                ahí la guía es lo que le va a servir el día que consiga una. */}
            {SENDS_TO_GUIDE.includes(assistant) ? (
                <section className="ob-connector -guide">
                    <p>{t(`onboarding.ia.guide.${assistant}${assistant === NO_ASSISTANT ? learnSuffix : ''}`)}</p>
                    {/* En otra pestaña: la guía es material de consulta PARA hacer este paso, y
                        abrirla acá lo sacaba del túnel en la mitad, con el asistente que había
                        elegido y sin camino de vuelta. Lo pidió el QA del 23/09. */}
                    <Link className="ob-btn -ghost" {...CLARITY_UNMASK}
                          to={assistant === 'claude_code' ? '/dashbo-mcp?assistant=claude_code' : '/dashbo-mcp'}
                          target="_blank" rel="noopener noreferrer">
                        {t('onboarding.ia.guide_cta')}
                    </Link>
                </section>
            ) : (
                <ol className="ob-connector">
                    {/* Con los nombres de los menús en negrita: son lo que el usuario busca en la
                        otra pantalla. Con HTML porque las claves no interpolan nada (ni texto del
                        usuario ni del backend). En un `<span>` porque el `<li>` es flex y cada `<b>`
                        suelto sería un ítem aparte, con su hueco. */}
                    <li>
                        <span className="ob-connector__text"
                              dangerouslySetInnerHTML={{__html: t(`onboarding.ia.step1.${assistant}`)}}/>
                    </li>
                    <li>
                        {t('onboarding.ia.step2')}
                        <code className="ob-code">{connectorUrl}</code>
                        {/* La dirección hay que PEGARLA en otra aplicación, así que transcribirla a
                            mano es el camino más probable al error —y el más silencioso: una URL
                            mal tipeada no falla acá, falla del otro lado y sin decir por qué. */}
                        <button type="button" className="ob-btn -mini -ghost"
                                onClick={() => copyAndFlag('url', connectorUrl, setCopiedUrl)}>
                            <Copy size={13}/> {copiedUrl ? t('onboarding.ia.copied_url')
                                                         : t('onboarding.ia.copy_url')}
                        </button>
                        {/* El resto del formulario del otro lado, que es donde se trabó fede en el
                            alta grabada (card J2tCKtaJ): qué nombre poner, qué dejar como viene y
                            qué cartel es normal. */}
                        {HAS_NAME_HINT.includes(assistant) && (
                            <span className="ob-connector__hint"
                                  dangerouslySetInnerHTML={{__html: t(`onboarding.ia.name_hint.${assistant}`)}}/>
                        )}
                        {/* Sólo donde el formulario tiene campo de ícono: el de Claude no lo tiene. */}
                        {HAS_ICON_FIELD.includes(assistant) && (
                            <span className="ob-connector__hint">
                                <a className="ob-link" href={DASHBO_ICON_URL} download="dashbo-icono.png">
                                    <DownloadSimple size={13}/> {t('onboarding.ia.logo_download')}
                                </a>
                                {' '}{t('onboarding.ia.logo_hint')}
                            </span>
                        )}
                    </li>
                    <li>{t('onboarding.ia.step3')}</li>
                </ol>
            )}

            {/* Qué plan hace falta de verdad. Una barrera inventada cuesta una conexión que sí se
                podía hacer, y una barrera omitida deja al usuario chocando contra una pantalla que
                no le habilita nada. */}
            {HAS_REQUIREMENT.includes(assistant) && (
                <p className="ob-requirement">{t(`onboarding.ia.requirement.${assistant}`)}</p>
            )}

            {/* Sin cliente no se muestra la pregunta. Con el nombre en blanco decía «¿Cómo viene el
                gasto de este mes?» con un doble espacio: la frase que tiene que sonar a la cartera
                del usuario terminaba sonando a demo. Un instante sin el bloque es mejor que eso. */}
            {question && !noAssistant && (
                <section className="ob-copyfield">
                    <span className="ob-copyfield__label">{t('onboarding.ia.try_this')}</span>
                    <p className="ob-copyfield__question">{question}</p>
                    {/* DÓNDE se pega. El rótulo «Prueba con esta» no lo dice, y la pregunta aparece
                        dentro de una pantalla de Dashbo: más de uno la buscó acá. La consulta va en
                        el asistente que se acaba de conectar, nunca en Dashbo. */}
                    <p className="ob-copyfield__where">
                        {t('onboarding.ia.ask_in', sentenceParts)}
                    </p>
                    {/* «Copiado» sólo se dice si se copió: en un contexto sin permiso de portapapeles
                        el botón mentía. Por `copyPlainText` y no por `navigator.clipboard` derecho:
                        ahí donde la API no existe, pegarle tira SINCRÓNICAMENTE dentro del onClick
                        —no copia, no enciende el error y no deja rastro—, y además hay un fallback
                        que sí copia. */}
                    {/* Al copiar, el botón no cambia de rótulo: al lado aparece qué hacer con lo
                        copiado (H-06, recorrido con Pablo del 13/10: «cuando pongo copiar, estaría
                        bueno que algo me diga: bueno, pégalo en Claude ahora»). Es el paso del
                        gancho de la pauta. Con `role="status"` para que también se oiga. */}
                    <div className="ob-copyfield__actions">
                        <button type="button" className="ob-btn -mini -ghost"
                                onClick={() => copyAndFlag('question', question, setCopied, PASTE_HINT_MS)}>
                            <Copy size={13}/> {t('onboarding.ia.copy')}
                        </button>
                        <span className="ob-copyfield__copied" role="status">
                            {copied && (
                                <>
                                    <CheckCircle size={14} weight="fill"/>
                                    {t('onboarding.ia.copied_paste_hint')}
                                </>
                            )}
                        </span>
                    </div>
                </section>
            )}

            {/* El estado de los datos va PRIMERO y la promesa de detección después: son dos
                relatos distintos —qué está pasando con la información, y qué pasa cuando la agencia
                pregunte— y mezclarlos en una sola frase los vuelve confusos. «Detectar» queda
                reservado para la primera consulta; los datos usan el verbo de preparación. */}
            {(syncMessageKey || statusAvailable) && !noAssistant && (
                <section className="ob-listening" aria-live="polite">
                    <div className="ob-listening__body">
                        {syncMessageKey && (
                            <p className="ob-listening__phase">
                                {/* El spinner acompaña al renglón que lo justifica, no al bloque
                                    entero: colgado del primero se leía «⟳ Tus datos ya están
                                    listos», girando al lado de un mensaje que no espera nada. Acá
                                    va sólo mientras la fase todavía se mueve. */}
                                {syncState?.keepPolling && <span className="ob-spinner-mini"/>}
                                {t(syncMessageKey, {count: syncState.total,
                                                    done: syncState.ready, total: syncState.total})}
                            </p>
                        )}
                        {/* Avisa, no bloquea: la pregunta de ejemplo sigue ahí arriba, copiable.
                            Sin esto, una respuesta pobre se lee como que el producto no sirve. */}
                        {warnsAboutMissingData(syncState) && (
                            <p className="ob-listening__warn">{t('onboarding.ia.sync.warn')}</p>
                        )}
                        {/* La agencia que YA tenía consultas se entera de eso, no de una
                            celebración: es verdad que su asistente preguntó, y es falso que
                            acabe de hacerlo. Lo segundo era lo que decía la pantalla.

                            ⚠️ **Acá no va un spinner.** Había uno girando al lado de «Esperando tu
                            primera consulta» y contaba una carga que no existe: lo que falta no lo
                            está trayendo el sistema, lo tiene que hacer la persona. Pablo, apenas
                            lo vio: «este cosito girando me hace sentir que hay algo que tiene que
                            cargar y que no va a cargar nunca… Él me está esperando a mí, pero yo,
                            cuando veo esto, me quedo esperando yo». Por eso el ícono es quieto y el
                            texto es una instrucción. El spinner de arriba sí se queda: ahí los
                            datos realmente se están trayendo. */}
                        {statusAvailable && (
                            <p className="ob-listening__waiting">
                                <Robot size={15}/>
                                {t(waitingKeyOf(firstQuery, question), sentenceParts)}
                            </p>
                        )}
                    </div>
                </section>
            )}

            {/* Los dos caminos del paso, y la pregunta que se interpone cuando el primero no se
                cumplió. Pablo marcó el riesgo de la versión dura —«no sé si está tan bueno obligar
                al usuario a que, para continuar, tenga que ir a trabajar a Claude o a ChatGPT»—,
                así que omitir es una salida de primera clase y no un castigo: lo que no puede pasar
                es que salir por ahí se cuente como una consulta que nunca existió. */}
            {/* `!noAssistant`: la pregunta es «¿la hacés ahora o seguís?», y a quien acaba de decir
                que no tiene asistente no se le puede preguntar eso. Sin esto, abrir la pregunta y
                después cambiar a «No tengo ninguna» la dejaba en pantalla. */}
            {asking && !noAssistant ? (
                <section className="ob-confirm" role="group" aria-labelledby="ob-confirm-title">
                    <p className="ob-confirm__title" id="ob-confirm-title">
                        <Question size={16}/> {t('onboarding.ia.confirm_title')}
                    </p>
                    <p className="ob-confirm__text">{t(`onboarding.ia.confirm_text${learnSuffix}`)}</p>
                    <div className="ob-confirm__actions">
                        <button type="button" className="ob-btn -ia" onClick={() => setAsking(false)} {...CLARITY_UNMASK}>
                            {t('onboarding.ia.confirm_do_it')}
                        </button>
                        <button type="button" className="ob-btn -ghost" onClick={leaveTunnel} {...CLARITY_UNMASK}>
                            {t('onboarding.ia.confirm_skip')}
                        </button>
                    </div>
                </section>
            ) : (
                <footer className="ob-foot">
                    {/* «Lo hago después» sobra cuando el paso YA está dado: no hay nada que
                        posponer, y ofrecerlo al lado de «este paso ya está dado» se contradice.
                        Ahí queda una sola salida. */}
                    {firstQuery !== 'prior' && (
                        <button type="button" className="ob-link -muted" onClick={leaveTunnel}>
                            {t('onboarding.ia.later')}
                        </button>
                    )}
                    {/* Sin asistente no hay consulta que verificar: el único botón honesto es el de
                        seguir. Ofrecerle «ya hice mi primera consulta» a quien acaba de decir que no
                        tiene con qué hacerla sería empujarlo a mentir para poder avanzar. */}
                    {noAssistant ? (
                        <button type="button" className="ob-btn -ia" onClick={leaveTunnel}>
                            {t('onboarding.ia.none_cta')}
                        </button>
                    ) : firstQuery === 'prior' ? (
                        <button type="button" className="ob-btn -ia" onClick={leaveTunnel}>
                            {t('onboarding.ia.already_queried_cta')}
                        </button>
                    ) : (
                        <button type="button" className="ob-btn -ia" disabled={checking}
                                onClick={confirmFirstQuery}>
                            {checking ? t('onboarding.ia.checking') : t('onboarding.ia.check_first_query')}
                        </button>
                    )}
                </footer>
            )}
        </div>
    );
}

/**
 * «¡Tu IA ya trabaja con tu cartera!»: el momento aha del alta, celebrado como tal (heWJCM8k, A + B).
 *
 * Era un aviso verde de 13 px al pie, con el mismo peso que un cartel informativo, debajo de un chat
 * INVENTADO: la pregunta sugerida y una respuesta fija. Agrandar la celebración agrandaba también
 * esa ficción, así que el chat se fue y quedan tres cosas verdaderas:
 *
 * - **el logro arriba y grande**, con la receta de `.sd-celebrate` del drawer: el círculo con el pop
 *   del tilde (`sheetImportCheckPop`), una ráfaga de 8 puntos sólo con CSS y el latido del último
 *   ítem. Todo el movimiento va bajo `prefers-reduced-motion: no-preference`;
 * - **lo que la agencia desbloqueó**, tildándose en cascada: plataformas, clientes y la consulta;
 * - **el recibo de la consulta real** (`detail`, contrato §3): desde qué asistente, hace cuánto y qué
 *   miró. Si fue lenguaje natural, la pregunta que de verdad hizo. Sin `detail` no hay recibo.
 */
function FirstQueryAnswered({detail, state, onFinish}) {
    const {t, i18n} = useTranslation();
    const {platforms, clients} = useUnlockedCounts(state);
    const receipt = receiptOf(detail, {t, language: i18n.language});
    const clientName = detail?.client_name || null;
    const queryText = typeof detail?.query_text === 'string' ? detail.query_text.trim() : '';

    const unlocked = [];
    if (platforms > 0) unlocked.push({key: 'platforms', text: t('onboarding.ia.first_query.unlocked_platforms', {count: platforms})});
    if (clients > 0) unlocked.push({key: 'clients', text: t('onboarding.ia.first_query.unlocked_clients', {count: clients})});
    if (clientName) {
        unlocked.push({key: 'query', text: t('onboarding.ia.first_query.unlocked_query',
                                             {client: clientName, interpolation: {escapeValue: false}})});
    }

    return (
        <div className="ob-step-body">
            <section className="ob-win" aria-live="polite">
                <span className="ob-win__badge" aria-hidden="true">
                    <span className="ob-win__burst">
                        {BURST_DOTS.map(index => <i key={index} style={{'--ob-k': index}}/>)}
                    </span>
                    <span className="ob-win__check"><CheckCircle size={40} weight="fill"/></span>
                </span>
                <h1 {...CLARITY_UNMASK}>{t('onboarding.ia.first_query.title')}</h1>
                {receipt && <p className="ob-win__receipt">{receipt}</p>}
                {/* La pregunta que HIZO, entre comillas (`<q>` las pone según el idioma de la
                    página). Sólo si la hubo: la sugerida mostrada como hecha era la ficción que
                    esta pantalla vino a sacar. */}
                {queryText && <p className="ob-win__question"><q>{queryText}</q></p>}
            </section>

            {unlocked.length > 0 && (
                <ul className="ob-unlocked">
                    {unlocked.map((item, index) => (
                        <li key={item.key} className={item.key === 'query' ? '-pulse' : undefined}
                            style={{'--ob-i': index}}>
                            <span className="ob-unlocked__check"><CheckCircle size={18} weight="fill"/></span>
                            <span>{item.text}</span>
                        </li>
                    ))}
                </ul>
            )}

            <footer className="ob-foot -center">
                <button type="button" className="ob-btn -primary" onClick={onFinish}>
                    {t('onboarding.ia.go_to_dashboard')}
                </button>
            </footer>
        </div>
    );
}

/** Los ocho puntos de la ráfaga: cada uno sale en su ángulo (`--ob-k` × 45°). */
const BURST_DOTS = [0, 1, 2, 3, 4, 5, 6, 7];

/**
 * Cuántas plataformas y cuántos clientes quedaron en marcha.
 *
 * Las plataformas se cuentan SIEMPRE sobre las credenciales (`agency/connections/`), con la misma
 * regla que el paso 2 (`connectedPlatformsCountOf`). Contar los canales del padrón dejaba afuera a
 * una plataforma conectada sin cuentas publicitarias: Google + LinkedIn vacío decía «1 plataforma
 * conectada» (review C-tunel-02).
 *
 * Los clientes, primero de lo que la pestaña ya sabe del recorrido; si no lo sabe —se llegó
 * recargando o desde el drawer—, se pregunta. Un número que no se pudo saber queda en 0 y su ítem no
 * se dibuja: la lista no afirma «0 plataformas conectadas».
 */
function useUnlockedCounts(state) {
    // Un número y no el objeto como dependencia: el efecto de abajo pide al servidor, y atado a la
    // identidad de `state` volvería a pedir con cada render.
    const tunnelClients = state?.apply?.createdClients?.length || 0;
    const [fetched, setFetched] = useState({platforms: 0, clients: 0});

    useEffect(() => {
        let alive = true;
        fetchConnections().then(response => {
            if (alive && response?.status === 'OK') {
                setFetched(previous => ({...previous, platforms: connectedPlatformsCountOf(response.data)}));
            }
        }).catch(() => {});
        if (!tunnelClients) {
            fetchAgencyClients().then(response => {
                if (!alive || response?.status !== 'OK') return;
                setFetched(previous => ({...previous, clients: agencyClientsOf(response).length}));
            }).catch(() => {});
        }
        return () => { alive = false; };
    }, [tunnelClients]);

    return {platforms: fetched.platforms, clients: tunnelClients || fetched.clients};
}

/** La lista de clientes de `agency/clients/`, venga pelada o envuelta. */
function agencyClientsOf(response) {
    if (Array.isArray(response?.data)) return response.data;
    return response?.data?.agency_clients || response?.data?.clients || [];
}

/** Cómo se nombra al asistente en el recibo. El que no reconocemos es «tu IA»: no se adivina. */
const ASSISTANT_NAME_KEYS = {
    claude: 'onboarding.ia.assistants.claude',
    chatgpt: 'onboarding.ia.assistants.chatgpt',
    claude_code: 'onboarding.ia.assistants.claude_code',
};

/** Qué hizo la IA, según la familia de la tool. Lo que no tiene frase propia «trabajó con» el cliente. */
const WHAT_KEYS = {
    indicators: 'onboarding.ia.first_query.what.indicators',
    data: 'onboarding.ia.first_query.what.data',
    budget: 'onboarding.ia.first_query.what.budget',
};

/**
 * «Consulta detectada · desde Claude · hace 12 s · miró el ritmo de Bakián».
 *
 * Sólo con `detail` y con el cliente resuelto: si el backend no pudo decir de qué cliente fue (el
 * cliente no es de la agencia, contrato §3), el recibo quedaría a medias, y la celebración igual se
 * sostiene sin él. Pura, para poder testearla sin montar la pantalla.
 */
export function receiptOf(detail, {t, language, now = Date.now()}) {
    const client = detail?.client_name;
    if (!client) return null;
    const assistantKey = ASSISTANT_NAME_KEYS[detail.assistant] || 'onboarding.ia.first_query.assistant_other';
    const whatKey = WHAT_KEYS[detail.kind] || 'onboarding.ia.first_query.what.other';
    const interpolation = {escapeValue: false};
    return t('onboarding.ia.first_query.receipt', {
        assistant: t(assistantKey),
        ago: agoText(detail.created_at, now, language),
        what: t(whatKey, {client, interpolation}),
        interpolation,
    });
}

/**
 * «hace 12 s», «hace 3 min», «ayer», en el idioma del usuario. Un reloj del navegador atrasado
 * respecto del servidor daría un tiempo negativo: se lee como «ahora», que es lo que pasó.
 */
export function agoText(createdAt, now, language) {
    const at = Date.parse(createdAt);
    const seconds = Number.isFinite(at) ? Math.max(0, Math.round((now - at) / 1000)) : 0;
    const format = new Intl.RelativeTimeFormat(language || 'es', {style: 'narrow', numeric: 'auto'});
    if (seconds < 60) return format.format(-seconds, 'second');
    if (seconds < 3600) return format.format(-Math.round(seconds / 60), 'minute');
    if (seconds < 86400) return format.format(-Math.round(seconds / 3600), 'hour');
    return format.format(-Math.round(seconds / 86400), 'day');
}

/**
 * El cliente que mejor sirve de ejemplo: el de mayor inversión de los que se acaban de crear.
 *
 * Preguntar por el cliente más grande es lo que hace que la respuesta sea interesante. Si no se
 * puede saber cuál es, se usa el primero — un nombre real cualquiera sigue siendo mejor que uno
 * inventado.
 */
export function biggestClientNameOf(state, server) {
    const groups = clientGroupsOf(state, server.candidates).assigned;
    let best = null;
    let bestAmount = -1;

    groups.forEach(group => {
        const total = group.accounts.reduce((sum, entry) => {
            const budget = server.budgets?.[String(entry.pk)];
            return sum + (budget?.amount || 0);
        }, 0);
        if (total > bestAmount) {
            bestAmount = total;
            best = group.name;
        }
    });

    return best || (groups[0] && groups[0].name) || '';
}
